import type { ReactNode } from 'react'

import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

export interface FieldShellProps {
  id: string
  label: string
  /** Marks the field required in the label. Validation itself belongs to the zod schema. */
  required?: boolean
  description?: string
  error?: string
  className?: string
  children: ReactNode
}

/**
 * Label, control, help text, and message in one consistent block.
 *
 * The error message is wired to the control through `aria-describedby` by the field components
 * that use this shell, and it replaces the description rather than stacking under it — a field
 * that is wrong should say one thing.
 */
export function FieldShell({
  id,
  label,
  required = false,
  description,
  error,
  className,
  children,
}: FieldShellProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={id}>
        {label}
        {required ? (
          <span className="text-danger-text" aria-hidden>
            *
          </span>
        ) : null}
      </Label>

      {children}

      {error ? (
        <p id={`${id}-error`} className="text-xs text-danger-text">
          {error}
        </p>
      ) : description ? (
        <p id={`${id}-description`} className="text-xs text-muted">
          {description}
        </p>
      ) : null}
    </div>
  )
}
